import { formatDateTime } from '@/lib/date-time';
/**
 * @file components/common/VersionBadge.tsx
 * @description 版本徽標：顯示前端構建版本，並拉 `/api/health` 比對後端版本。
 *
 * 用途：部署後在頁面上一眼確認「前後端是否都更新成功 / 瀏覽器是否還在用舊快取」。
 * - 綠點：前後端 git sha 一致。
 * - 紅點：前後端版本不一致（多半是瀏覽器快取舊 JS，或只重啟了一邊）→ 提示硬刷新。
 * - 黃點：後端離線 / 無回應。
 * - 灰點：開發模式或無法比對（任一邊為 dev）。
 *
 * 放置於側邊欄底部（BaseLayout，覆蓋所有角色）與登入頁頁腳。
 */

import { Tooltip } from 'antd';
import { useEffect, useState } from 'react';
import { appVersion } from '@/lib/app-version';
import { color, font, text } from '@/styles/tokens';

/** 後端 /api/health 回傳（data 部分） */
interface BackendHealth {
  ok: boolean;
  service: string;
  version?: string;
  gitSha?: string;
  builtAt?: string;
  startedAt?: string;
}

/** ISO 時間格式化為短日期時間；非法值原樣返回 */
function fmt(iso?: string): string {
  if (!iso || iso === 'dev') return '開發模式';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : formatDateTime(d);
}

/**
 * 版本徽標組件。
 * @param dark      深色側邊欄（影響文字配色）
 * @param collapsed 側邊欄收合時只顯示狀態點
 */
export function VersionBadge({ dark, collapsed }: { dark?: boolean; collapsed?: boolean }) {
  const [backend, setBackend] = useState<BackendHealth | null>(null);
  const [offline, setOffline] = useState(false);

  useEffect(() => {
    let alive = true;
    // /api/health 為公開端點（無需登入），失敗時標記後端離線
    fetch('/api/health', { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j: { success?: boolean; data?: BackendHealth } | null) => {
        if (!alive) return;
        if (j?.success && j.data) setBackend(j.data);
        else setOffline(true);
      })
      .catch((err) => {
        console.error('[version-badge] 查詢後端版本失敗:', err);
        if (alive) setOffline(true);
      });
    return () => {
      alive = false;
    };
  }, []);

  const feSha = appVersion.gitSha;
  const beSha = backend?.gitSha;
  /** 前後端皆有真實 sha 才能比對 */
  const comparable = feSha !== 'dev' && !!beSha && beSha !== 'dev';
  const mismatch = comparable && feSha !== beSha;

  const dotColor = offline
    ? color.state.warning
    : mismatch
      ? color.state.danger
      : comparable
        ? color.state.success
        : color.text.muted;

  const mutedColor = dark ? color.sidebar.textMuted : color.text.tertiary;

  const tooltip = (
    <div style={{ fontSize: 12, lineHeight: 1.7, fontFamily: font.mono }}>
      <div>
        前端 v{appVersion.version} · {feSha}
      </div>
      <div>構建 {fmt(appVersion.builtAt)}</div>
      <div style={{ borderTop: '1px solid rgba(255,255,255,0.18)', margin: '5px 0' }} />
      {offline ? (
        <div>後端 離線 / 無回應</div>
      ) : backend ? (
        <>
          <div>
            後端 v{backend.version ?? '—'} · {beSha ?? '—'}
          </div>
          <div>構建 {fmt(backend.builtAt)}</div>
          <div>啟動 {fmt(backend.startedAt)}</div>
        </>
      ) : (
        <div>後端 查詢中…</div>
      )}
      <div style={{ borderTop: '1px solid rgba(255,255,255,0.18)', margin: '5px 0' }} />
      <div>
        {offline
          ? '⚠ 無法連到後端'
          : mismatch
            ? '⚠ 前後端版本不一致，請硬刷新（Cmd/Ctrl+Shift+R）'
            : comparable
              ? '✓ 前後端版本一致'
              : '開發模式 / 無法比對'}
      </div>
    </div>
  );

  return (
    <Tooltip title={tooltip} placement="right">
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          justifyContent: collapsed ? 'center' : 'flex-start',
          padding: collapsed ? '6px 8px' : '6px 14px',
          cursor: 'default',
          userSelect: 'none',
        }}
      >
        <span
          style={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            background: dotColor,
            flexShrink: 0,
          }}
        />
        {!collapsed && (
          <span
            style={{
              fontSize: text.xs,
              color: mutedColor,
              fontFamily: font.mono,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            v{appVersion.version} · {feSha}
          </span>
        )}
      </div>
    </Tooltip>
  );
}
